import { ReactNode, useCallback, useMemo } from 'react'
// Material
import { Skeleton, styled } from '@mui/material'
import { getFileUrl } from '@weblite/file'
// Logic
import { getAvatarText } from '$func/avatar-text'
import { OpenStorySource } from '$logic/story/analytics'
import { AVATAR_SIZES } from '$common/avatar/avatar-base/conf'
import { SelectableAvatar, SelectableAvatarProps } from '$common/avatar'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { showImageViewerModal } from '$comp/modals/image-viewer'
import { useWorkspaceById } from '$logic/workspace'

interface Props
  extends Omit<
    SelectableAvatarProps,
    | 'avatarBackgroundText'
    | 'alt'
    | 'storyCount'
    | 'seenStoryCount'
    | 'src'
    | 'storyLoading'
  > {
  workspaceId: string
  overrideImage?: string
  selected?: boolean
  children?: ReactNode
  disableClick?: boolean
  source?: OpenStorySource
  showBackdrop?: boolean
}

/**
 * - TODO: background and color
 * - NOTE: Mui uses the first letter of alt in broken links
 */
export function WorkspaceAvatar({
  overrideImage,
  workspaceId,
  disableClick = false,
  onClick,
  children = null,
  checked = false,
  source,
  size = AvatarSize.SM,
  showBackdrop = false,
  ...rest
}: Props) {
  const workspace = useWorkspaceById(workspaceId)

  const { icon } = workspace || {}
  const avatarSrc = getFileUrl(
    overrideImage !== undefined ? overrideImage : icon,
  )

  const openImageViewerHandler = useCallback(() => {
    if (avatarSrc) showImageViewerModal({ media: { src: avatarSrc } })
  }, [avatarSrc])

  const handleClick = useMemo(() => {
    if (disableClick) return undefined
    return openImageViewerHandler
  }, [])

  if (!workspace)
    return (
      <Skeleton
        variant="circular"
        width={AVATAR_SIZES[size]}
        height={AVATAR_SIZES[size]}
      />
    )

  return (
    <SelectableAvatar
      avatarBackgroundText={children || getAvatarText(workspace?.name)}
      src={avatarSrc}
      alt={workspace?.name.toUpperCase()}
      checked={checked}
      backgroundColor={workspace.color}
      size={size}
      sx={{ zIndex: 2 }}
      squareMode
      backdrop={showBackdrop ? <Backdrop /> : null}
      {...rest}
    />
  )
}

const Backdrop = styled('div')`
  width: 40px;
  height: 40px;
  background-color: ${({ theme }) => theme.palette.neutral000};
  opacity: 0.25;
  position: absolute;
  transform-origin: bottom left;
  transform: rotate(15deg);
  border-radius: 11px;
`
